import Card from 'components/card';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { deleteProduction, getProductions } from 'interfaces/productions';
import PlannedProductionItem from './components/PlannedProductionItem';
import PlannedProductionPreviewModal from './components/productionPreview';
import { useDisclosure } from '@chakra-ui/hooks';
import DeleteModal from 'components/actions/Delete';
import { MdFactory } from 'react-icons/md';

const PlannedProductions = () => {
    const [productions, setProductions] = useState<any>([]);
    const [production, setProduction] = useState<any>(null);
    const [toDelete, setToDelete] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const nav = useNavigate();
    const {
        isOpen: isOpenDelete,
        onOpen: onOpenDelete,
        onClose: onCloseDelete,
    } = useDisclosure();

    const initProductions = async () => {
        setIsLoading(true);
        const prods = await getProductions();
        if (!prods) {
            nav('/auth/login');
            return;
        }
        setProductions(prods.data);
        setIsLoading(false);
    };

    const delProduction = async (id: string) => {
        const rest = await deleteProduction(id);
        if (!rest) return;
        if (rest.error) {
            console.log(rest.error);
            return;
        }
        onCloseDelete();
        const remaining = productions.filter(
            (p: any) => p._id !== id && p.status === 'scheduled',
        );
        if (remaining.length <= 0) {
            nav('/stock/production');
            return;
        } else setProductions(remaining);
    };

    useEffect(() => {
        initProductions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleView = (prod: any) => {
        setProduction(prod);
        onOpen();
    };

    const handleDeleteClick = (prod: any) => {
        setToDelete(prod);
        onOpenDelete();
    };

    const scheduledProductions = productions.filter((p: any) => p.status === 'scheduled');
    const totalQuantity = scheduledProductions.reduce((acc: number, p: any) => acc + (p.quantity || 0), 0);

    return (
        <div className="w-full h-full min-h-[85vh]">
            {/* Header Section */}
            <Card extra="w-full p-5 mb-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
                            Vos productions planifiées
                        </h2>
                        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                            Gérez et lancez vos productions prêtes à être réalisées
                        </p>
                    </div>
                    {scheduledProductions.length > 0 && (
                        <div className="hidden md:flex items-center gap-4">
                            <div className="text-center">
                                <p className="text-xs text-gray-500 dark:text-gray-400">Productions</p>
                                <p className="text-2xl font-bold text-toola-500">
                                    {scheduledProductions.length}
                                </p>
                            </div>
                            {totalQuantity > 0 && (
                                <div className="text-center">
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Unités</p>
                                    <p className="text-2xl font-bold text-toola-500">
                                        {totalQuantity}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Card>

            <PlannedProductionPreviewModal
                isOpen={isOpen}
                onClose={onClose}
                production={production}
            />

            <DeleteModal
                isOpen={isOpenDelete}
                onClose={onCloseDelete}
                description="Êtes-vous sûr de vouloir supprimer cette production ?"
                context="de production"
                deleteFunction={() => {
                    delProduction(toDelete._id);
                }}
            />

            {/* Liste des productions */}
            <Card extra="w-full p-5 border border-gray-200 dark:border-navy-700">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-20">
                        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gray-200 border-t-toola-500"></div>
                        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Chargement des productions...</p>
                    </div>
                ) : scheduledProductions.length === 0 ? (
                    <div className="flex w-full flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 py-20 dark:border-navy-600 dark:bg-navy-800/50">
                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-navy-700">
                            <MdFactory className="h-8 w-8 text-gray-400 dark:text-gray-500" />
                        </div>
                        <p className="mt-4 text-base font-bold text-gray-700 dark:text-gray-300">
                            Aucune production planifiée
                        </p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                            Planifiez une nouvelle production pour commencer
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                        {scheduledProductions.map((production: any) => (
                            <PlannedProductionItem
                                key={production._id}
                                production={production}
                                onView={() => handleView(production)}
                                onDelete={() => handleDeleteClick(production)}
                            />
                        ))}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default PlannedProductions;
